import { axios } from '@argon/app/axios'
import moment from 'moment'
import { zipWith } from 'lodash/array'
import { some } from 'lodash/collection'
import { isEmpty } from 'lodash/lang'
import { filterPolicies } from '../utils'

const NAMESPACE = 'me'
const VERIFY = 'verify'
const verifyNotification = {
  namespace: NAMESPACE,
  name: VERIFY,
  component: () => import('./components/VerifyNotification')
}
const INVITATION = 'invitation'
const invitationNotification = {
  namespace: NAMESPACE,
  name: INVITATION,
  component: () => import('./components/InvitationNotification')
}

const state = {
  lastQuery: null,
  status: '',
  userId: null,
  profile: null,
  account: null,
  claims: [],
  policies: [],
  organizations: [],
  invitations: [],
  pendingInvitations: [],
  settings: { language: null }
}

const getters = {
  claims: (state) => state.claims,
  policies: (state) => state.policies,
  profile: (state) => state.profile,
  settings: (state) => state.settings,
  organizations: (state) => state.organizations,
  invitations: (state) => state.invitations,
  locale: (state) => state.settings.language || process.env.VUE_APP_I18N_LOCALE || 'en',
  timezone: (state) => state.settings.timezone || process.env.VUE_APP_TZ || 'Canada/Eastern'
}

const mutations = {
  SET_LAST_QUERY(state, payload) {
    state.lastQuery = payload
  },
  SET_STATUS(state, payload) {
    state.status = payload
  },
  SET_USER_ID: (state, payload) => {
    state.userId = payload
  },
  SET_PROFILE: (state, payload) => {
    state.profile = payload
  },
  SET_ACCOUNT: (state, payload) => {
    state.account = payload
  },
  SET_CLAIMS: (state, payload) => {
    state.claims = payload
  },
  SET_POLICIES: (state, payload) => {
    state.policies = payload
  },
  SET_ORGANIZATIONS: (state, payload) => {
    state.organizations = payload
  },
  SET_SETTINGS: (state, payload) => {
    state.settings = payload || {}
  },
  SET_INVITATIONS: (state, payload) => {
    state.invitations = payload
  },
  SET_PENDING_INVITATIONS: (state, payload) => {
    state.pendingInvitations = payload
  },
  SET_CURRENT_ORG: (state, payload) => {
    state.currentOrgId = payload
  }
}

const actions = {
  setLocale: ({ commit, state }, language) => {
    return new Promise((resolve, reject) => {
      let settings = { ...state.settings, language }
      commit('SET_SETTINGS', settings)
      resolve(true)
    })
  },
  setCurrentOrg: ({ commit }, id) => {
    return new Promise((resolve, reject) => {
      commit('SET_CURRENT_ORG', id)
      resolve(true)
    })
  },
  notifyVerification({ dispatch }, account) {
    return new Promise((resolve, reject) => {
      if ((account.email && account.email_verified) || (account.phone && account.phone_verified)) {
        dispatch('app/removeNotificationByName', VERIFY, {
          root: true
        })
      } else {
        dispatch('app/replaceNotificationsByName', verifyNotification, { root: true })
      }
      resolve(account)
    })
  },
  notifyInvitation({ dispatch, state }) {
    return new Promise((resolve, reject) => {
      if (state.pendingInvitations.length === 0) {
        dispatch('app/removeNotificationByName', INVITATION, {
          root: true
        })
      } else {
        dispatch('app/replaceNotificationsByName', invitationNotification, { root: true })
      }
      resolve(true)
    })
  },
  setProfile: ({ commit, dispatch, state }, payload) => {
    const { user, account, claims, policies, organizations, settings } = payload
    let filtered = filterPolicies(claims, policies)
    return new Promise((resolve, reject) => {
      commit('SET_USER_ID', user.id)
      commit('SET_PROFILE', user)
      let profileFetcher = () => dispatch('getProfile', {})
      let profileData = {
        category: 'profiles',
        identifier: user.refid,
        fetcher: profileFetcher
      }
      dispatch('realtime/addListener', profileData, { root: true })
      let inviteFetcher = () => dispatch('listInvitations')
      let inviteData = {
        category: 'userInvitations',
        identifier: user.refid,
        fetcher: inviteFetcher
      }
      dispatch('realtime/addListener', inviteData, { root: true })
      commit('SET_ACCOUNT', account)
      commit('SET_ORGANIZATIONS', organizations)
      commit('SET_SETTINGS', settings || state.settings)
      commit('SET_CLAIMS', claims || state.claims)
      commit('SET_POLICIES', filtered || state.policies)
      commit('SET_LAST_QUERY', moment())
      dispatch('dumpProfile')
        .then(() => {
          return dispatch('notifyVerification', account)
        })
        .then(resolve)
        .catch(reject)
    })
  },
  getProfile: ({ dispatch }, payload) => {
    const { include } = payload
    let params = include
      ? { include: `${include},organizations,settings`, refresh: true }
      : { include: 'organizations,settings', refresh: true }
    return new Promise((resolve, reject) => {
      axios({
        url: '/me',
        params,
        method: 'GET'
      })
        .then(({ data }) => {
          return dispatch('setProfile', data)
        })
        .then(resolve)
        .catch(reject)
    })
  },
  patchUser: ({ dispatch }, payload) => {
    const { data } = payload
    return new Promise((resolve, reject) => {
      axios({
        url: '/me',
        data,
        method: 'PATCH'
      })
        .then(() => {
          return dispatch('getProfile')
        })
        .then((result) => {
          return dispatch('app/setContextObj', { ...result.user, type: 'user' }, { root: true })
        })
        .then(() => {
          resolve(data)
        })
        .catch(reject)
    })
  },
  patchAccount: ({ commit, dispatch }, payload) => {
    const { data } = payload
    return new Promise((resolve, reject) => {
      axios({
        url: '/account',
        data,
        method: 'PATCH'
      })
        .then(({ data }) => {
          commit('SET_ACCOUNT', data)
          return dispatch('notifyVerification', data)
        })
        .then(resolve)
        .catch(reject)
    })
  },
  patchSettings: ({ dispatch }, data) => {
    return new Promise((resolve, reject) => {
      axios({
        url: '/me/settings',
        data,
        method: 'PATCH'
      })
        .then(({ data }) => {
          dispatch('getProfile', {})
          resolve(data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  dumpProfile: ({ state }) => {
    return new Promise((resolve, reject) => {
      const { profile, context, account, organizations, settings } = state
      let toStore = { profile, context, account, organizations, settings }
      localStorage.setItem('_argon_profile', JSON.stringify(toStore))
      resolve(true)
    })
  },
  loadProfile: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      let stored = localStorage.getItem('_argon_profile')
      if (stored) {
        let payload = JSON.parse(stored)
        const { profile, claims, policies, account, organizations, settings } = payload
        commit('SET_USER_ID', profile.id)
        commit('SET_PROFILE', profile)
        commit('SET_ACCOUNT', account)
        commit('SET_ORGANIZATIONS', organizations)
        commit('SET_SETTINGS', settings)
        commit('SET_CLAIMS', claims)
        commit('SET_POLICIES', policies)
        dispatch('app/setContextObj', { ...profile, type: 'user' }, { root: true })
        dispatch('notifyVerification', account)
        resolve(true)
      } else {
        dispatch('getProfile', {})
      }
    })
  },
  updateOrganizations: ({ commit, state }, updated) => {
    return new Promise((resolve, reject) => {
      let shouldAppend = some(state.organizations, { id: updated.id })
      let organizations = shouldAppend
        ? state.organizations.map((x) => (x.id === updated.id ? updated : x))
        : [...state.organizations, updated]
      commit('SET_ORGANIZATIONS', organizations)
      resolve(organizations)
    })
  },
  clearProfile: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      localStorage.removeItem('_argon_profile')
      commit('SET_LAST_QUERY', null)
      commit('SET_PROFILE', null)
      commit('SET_ACCOUNT', null)
      commit('SET_ORGANIZATIONS', [])
      commit('SET_CLAIMS', [])
      commit('SET_POLICIES', [])
      dispatch('realtime/clearListeners', {}, { root: true })
      resolve(true)
    })
  },
  uploadAvatar: ({ commit, dispatch }, data) => {
    let url = '/me/avatar'
    return new Promise((resolve, reject) => {
      axios({
        url,
        data,
        method: 'POST'
      })
        .then(({ data }) => {
          commit('SET_PROFILE', data)
          dispatch('dumpProfile')
          resolve(data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  listInvitations: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      axios({
        url: '/invitations',
        params: { include: 'organization' },
        method: 'GET'
      })
        .then(({ data }) => {
          commit('SET_INVITATIONS', data.items)
          let pending = data.items.filter((x) => !x.resolved)
          commit('SET_PENDING_INVITATIONS', pending)
          dispatch('notifyInvitation')
          resolve(data.items)
        })
        .catch(reject)
    })
  },
  getInviteByRefid: ({ state }, refid) => {
    return new Promise((resolve, reject) => {
      let match = state.invitations.find((invite) => invite.refid === refid)
      if (match) {
        return resolve(match)
      }
      return reject(new Error('No match found'))
    })
  },
  acceptInvitation: ({ dispatch }, invitation) => {
    return new Promise((resolve, reject) => {
      axios({
        url: `/invitations/${invitation.refid}/accept`,
        data: {},
        method: 'POST'
      })
        .then(() => {
          return dispatch('listInvitations')
        })
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  rejectInvitation: ({ dispatch }, invitation) => {
    return new Promise((resolve, reject) => {
      axios({
        url: `/invitations/${invitation.refid}/reject`,
        data: {},
        method: 'POST'
      })
        .then(() => {
          return dispatch('listInvitations')
        })
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  resendVerification: ({}) => {
    return new Promise((resolve, reject) => {
      let url = '/request-verify'
      axios({
        url,
        data: {},
        method: 'POST'
      })
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export { NAMESPACE }
